import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { ReactElement } from 'react';
import TypographyLink from '@ui/TypographyLink';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import {
  useIsBusiness,
  useIsManagedUser,
  useIsTrialUser,
} from 'context/user/UserContext';
import { usePromptsContext } from 'context/prompts/PromptsContext';

const useStyles = makeStyles()(theme => ({
  iconWrapper: {
    height: theme.spacing(2.25),
    marginRight: theme.spacing(1.25),
  },
  warningColor: {
    borderRadius: '33.333px',
    background: theme.palette.error.light,
    color: theme.palette.grey[900],
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

interface EditorErrorProps {
  inlineError: string | null | ReactElement;
  tooltip?: string;
  upgradePlanError?: boolean | null;
}

const EditorError = (props: EditorErrorProps) => {
  const { inlineError, tooltip, upgradePlanError } = props;
  const { classes } = useStyles();
  const showError = !!inlineError;
  const isTrialUser = useIsTrialUser();
  const isBusinessTier = useIsBusiness();
  const isManagedUser = useIsManagedUser();
  const [, dispatchPrompt] = usePromptsContext();

  // If tooltip text exists, add tabIndex, otherwise don't set so that focus is ignored
  const shouldFocusOnTooltip = tooltip ? 0 : undefined;
  return showError ? (
    <Box className={classes.container}>
      <Tooltip tabIndex={shouldFocusOnTooltip} title={tooltip || ''}>
        <Box className={classes.iconWrapper}>
          <Icon type="exclamation02" className={classes.warningColor} />
        </Box>
      </Tooltip>
      <Box>
        <Typography variant="pMd" role="alert" bold>
          {inlineError}
        </Typography>
        {upgradePlanError &&
          (!(isBusinessTier || isManagedUser) ? (
            <Typography variant="pMd" role="alert">
              <TypographyLink
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  dispatchPrompt({
                    type: 'OpenSubscriptionTiersPrompt',
                    payload: {
                      upgradeContext: 'access_limit_clips',
                      upgradePath: 'Dashboard > Studio > Clips Limit Modal',
                    },
                  });
                }}
                variant={'pMd'}
                color="primary"
                href={''}
              >
                Upgrade
              </TypographyLink>
              {isTrialUser
                ? ' to keep creating and unlock pro-level features.'
                : ' your plan for more downloads or wait for your limit to reset.'}
            </Typography>
          ) : (
            <Typography variant="pMd" role="alert">
              You may not be on the right plan! &nbsp;
              <TypographyLink
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {}}
                variant={'pMd'}
                color="primary"
                href={'https://www.wellsaid.io/contact-sales'}
              >
                Contact sales
              </TypographyLink>
              &nbsp;to get more downloads.
            </Typography>
          ))}
      </Box>
    </Box>
  ) : null;
};

export default EditorError;
